import { useState } from "react";
import { Link } from "@tanstack/react-router";
import type { Rolle } from "@cubic/shared";
import { Button } from "@/components/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/Dialog";
import { Fehlerzeile } from "@/components/Fehlerzeile";
import { Label } from "@/components/Label";
import { Textarea } from "@/components/Textarea";
import { Flaeche } from "@/components/entwurf/Flaeche";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";
import { Rubrik } from "@/components/entwurf/Rubrik";
import { Zustandsmarke } from "@/components/entwurf/Zustandspunkt";
import { STAND, type Stand } from "@/lib/entwurf-zustaende";
import {
  fehlerMeldung,
  useIch,
  useMergeRequestMergen,
  useQualitaetsGatePruefen,
  useQualitaetsGateStand,
  useQualitaetsGateUebersteuern,
} from "@/lib/haken";
import { qualitaetsGateAnzeige } from "@/lib/qualitaets-gate-zustand";
import {
  darfMergeRequestMergen,
  darfQualitaetsGatePruefen,
  darfQualitaetsGateUebersteuern,
} from "@/lib/rollen";
import { cx, focusRing } from "@/lib/utils";

const PIPELINE_LAEUFT = new Set([
  "created",
  "waiting_for_resource",
  "preparing",
  "pending",
  "running",
  "canceling",
  "scheduled",
]);

function formatiereZeitpunkt(
  wert: string | Date | null | undefined,
): string {
  if (!wert) return "—";
  return new Date(wert).toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function pipelineStand(zustand: string): Stand {
  if (zustand === "success") return "ausgefuehrt";
  if (PIPELINE_LAEUFT.has(zustand)) return "laeuft";
  if (zustand === "canceled" || zustand === "cancelled") {
    return "abgebrochen";
  }
  if (zustand === "skipped" || zustand === "manual") return "pausiert";
  return "gescheitert";
}

function pipelineLabel(zustand: string): string {
  if (zustand === "success") return "Erfolgreich";
  if (zustand === "running") return "Läuft";
  if (PIPELINE_LAEUFT.has(zustand)) return "Wartet";
  if (zustand === "canceled" || zustand === "cancelled") {
    return "Abgebrochen";
  }
  if (zustand === "failed") return "Fehlgeschlagen";
  if (zustand === "skipped") return "Übersprungen";
  if (zustand === "manual") return "Manuell";
  return zustand || "Unbekannt";
}

function autoQaStand(zustand: string): Stand {
  switch (zustand) {
    case "gebremst":
    case "wartet":
    case "laeuft":
    case "ausgefuehrt":
    case "gescheitert":
    case "abgebrochen":
      return zustand;
    case "fertig":
      return "ausgefuehrt";
    case "fertig_mit_aufhaltern":
      return "blockiert";
    default:
      return "ausstehend";
  }
}

function autoQaLabel(zustand: string): string {
  if (zustand === "gebremst") return "Sammelt";
  if (zustand === "wartet") return "Wartet";
  if (zustand === "laeuft") return "Läuft";
  if (zustand === "ausgefuehrt") return "Ausgeführt";
  if (zustand === "gescheitert") return "Gescheitert";
  if (zustand === "abgebrochen") return "Abgebrochen";
  if (zustand === "fertig") return "Fertig";
  if (zustand === "fertig_mit_aufhaltern") return "Fertig mit Aufhaltern";
  return zustand || "Unbekannt";
}

function grundStand(art: string): Stand {
  if (art === "blockiert") return "blockiert";
  if (art === "nicht_bewertbar") return "gescheitert";
  return "wartet";
}

function grundLabel(art: string): string {
  if (art === "blockiert") return "Blockiert";
  if (art === "nicht_bewertbar") return "Nicht bewertbar";
  return "Ausstehend";
}

function Marke({ stand, label }: { stand: Stand; label: string }) {
  const bild = STAND[stand];
  return (
    <Zustandsmarke
      bild={bild}
      label={label}
      ton={bild.text}
      strich={bild.strich}
    />
  );
}

export function QualitaetsGateAbschnitt({
  projektId,
  mrId,
}: {
  projektId: string;
  mrId: string;
}) {
  const stand = useQualitaetsGateStand(mrId);
  const ich = useIch();
  const pruefen = useQualitaetsGatePruefen(projektId, mrId);
  const mergen = useMergeRequestMergen(projektId, mrId);
  const uebersteuern = useQualitaetsGateUebersteuern(projektId, mrId);
  const [dialogOffen, setzeDialogOffen] = useState(false);
  const [mergeDialogOffen, setzeMergeDialogOffen] = useState(false);
  const [mergeWiederholungsschluessel, setzeMergeWiederholungsschluessel] =
    useState("");
  const [begruendung, setzeBegruendung] = useState("");
  const [ausnahmeZiel, setzeAusnahmeZiel] = useState<{
    pruefungId: string;
    kopfSha: string | null;
  } | null>(null);
  const abschnittId = `qualitaets-gate-${mrId}`;

  if (stand.isLoading && !stand.data) {
    return (
      <section aria-labelledby={abschnittId} aria-busy="true">
        <Rubrik>
          <span id={abschnittId}>Quality Gate</span>
        </Rubrik>
        <Mikrolabel grad={11.5} className="mt-4 block">
          Lade Gate-Stand …
        </Mikrolabel>
      </section>
    );
  }

  if (!stand.data) {
    return (
      <section aria-labelledby={abschnittId}>
        <Rubrik>
          <span id={abschnittId}>Quality Gate</span>
        </Rubrik>
        <div className="mt-4">
          <Fehlerzeile
            meldung={fehlerMeldung(stand.error)}
            onErneut={() => void stand.refetch()}
          />
        </div>
      </section>
    );
  }

  const daten = stand.data;
  const anzeige = qualitaetsGateAnzeige({
    modus: daten.regel.modus,
    entscheidung: daten.ergebnis.entscheidung,
    darfMergen: daten.ergebnis.darfMergen,
    haetteBlockiert: daten.ergebnis.haetteBlockiert,
  });
  const bild = STAND[anzeige.stand];
  const pruefung = daten.aktuellePruefung ?? daten.letztePruefung;
  const rolle = ich.data?.projekte.find(
    (eintrag) => eintrag.projektId === projektId,
  )?.rolle as Rolle | undefined;
  const darfUebersteuern = darfQualitaetsGateUebersteuern(rolle, {
    rechte: ich.data?.rechte ?? [],
  });
  const darfPruefen = darfQualitaetsGatePruefen(rolle);
  const darfMergen = darfMergeRequestMergen(rolle, {
    rechte: ich.data?.rechte ?? [],
  });
  const istOffen = daten.mergeRequest.zustand.toLowerCase() === "opened";
  const istUebersteuerbar =
    daten.regel.modus === "blockierend" &&
    daten.aktuellePruefung !== null &&
    daten.uebersteuerung === null &&
    (daten.ergebnis.urspruenglicheEntscheidung === "blockiert" ||
      daten.ergebnis.urspruenglicheEntscheidung === "nicht_bewertbar");
  const begruendungLaenge = begruendung.trim().length;
  const begruendungGueltig =
    begruendungLaenge >= 10 && begruendungLaenge <= 2_000;

  const dialogAendern = (offen: boolean) => {
    setzeDialogOffen(offen);
    if (!offen) {
      setzeBegruendung("");
      setzeAusnahmeZiel(null);
      uebersteuern.reset();
    }
  };

  return (
    <section aria-labelledby={abschnittId}>
      <Rubrik
        aktion={
          <span aria-live="polite">
            <Zustandsmarke
              bild={bild}
              label={anzeige.label}
              ton={bild.text}
              strich={bild.strich}
            />
          </span>
        }
      >
        <span id={abschnittId}>Quality Gate</span>
      </Rubrik>

      <Flaeche className="mt-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-[62ch]">
            <p className="text-[13.5px] leading-relaxed text-tinte">
              {anzeige.erklaerung}
            </p>
            <Mikrolabel grad={11} className="mt-2 block">
              {daten.regel.modus === "blockierend"
                ? daten.ergebnis.darfMergen
                  ? "Merge freigegeben"
                  : "Merge gesperrt"
                : "Merge wird durch diesen Modus nicht gesperrt"}
              {daten.mergeRequest.kopfSha
                ? ` · Kopf ${daten.mergeRequest.kopfSha.slice(0, 8)}`
                : " · Kopf-SHA fehlt"}
            </Mikrolabel>
          </div>
          <Mikrolabel grad={11} className="tabular-nums">
            Regel v{daten.regel.version}
            {daten.regel.istVorgabe ? " · Vorgabe" : ""}
          </Mikrolabel>
        </div>

        <dl className="mt-5 grid gap-3 text-[12.5px] sm:grid-cols-2">
          <div>
            <dt className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-tinte4">
              Prüfung
            </dt>
            <dd className="mt-1 text-tinte2">
              {pruefung
                ? formatiereZeitpunkt(pruefung.geprueftAm)
                : "Noch kein Prüfschnappschuss"}
              {pruefung && !daten.aktuellePruefung
                ? " · älterer Signalstand"
                : ""}
            </dd>
          </div>
          <div>
            <dt className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-tinte4">
              Pflichtsignale
            </dt>
            <dd className="mt-1 text-tinte2">
              {[
                daten.regel.pipelineErforderlich ? "Pipeline" : null,
                daten.regel.autoQaErforderlich ? "Auto Q&A" : null,
              ]
                .filter(Boolean)
                .join(" · ") || "Keine"}
            </dd>
          </div>
        </dl>
      </Flaeche>

      {stand.isError && stand.failureCount >= 4 ? (
        <div className="mt-4">
          <Fehlerzeile
            meldung={fehlerMeldung(stand.error)}
            onErneut={() => void stand.refetch()}
          />
        </div>
      ) : null}

      <div className="mt-5">
        <Mikrolabel grad={11.5} ton="laut" betont>
          Signale
        </Mikrolabel>
        <div className="mt-1">
          <div className="flex min-h-11 flex-wrap items-center gap-3 border-b border-linie py-2">
            {daten.regel.pipelineErforderlich ? (
              daten.pipeline ? (
                <Marke
                  stand={pipelineStand(daten.pipeline.zustand)}
                  label={`Pipeline #${daten.pipeline.gitlabId} · ${pipelineLabel(
                    daten.pipeline.zustand,
                  )}`}
                />
              ) : (
                <Marke stand="ausstehend" label="Pipeline fehlt" />
              )
            ) : (
              <Marke stand="uebersprungen" label="Pipeline nicht erforderlich" />
            )}
            <Link
              to="/projekte/$id"
              params={{ id: projektId }}
              search={{ reiter: "lieferung", lieferung: "pipelines" }}
              className={cx(
                "ml-auto font-mono text-[11px] text-akzent underline decoration-1 underline-offset-[3px]",
                focusRing,
              )}
            >
              Pipelines öffnen
            </Link>
          </div>

          {daten.regel.autoQaErforderlich ? (
            daten.regel.automationIds.length > 0 ? (
              <ul role="list">
                {daten.regel.automationIds.map((automationId) => {
                  const signal = daten.autoQa.find(
                    (eintrag) => eintrag.automationId === automationId,
                  );
                  const fehlgrund = daten.ergebnis.gruende.find(
                    (grund) =>
                      grund.bezugId === automationId &&
                      grund.code === "auto_qa_fehlt",
                  );
                  return (
                    <li
                      key={automationId}
                      className="flex min-h-11 flex-wrap items-center gap-3 border-b border-linie py-2"
                    >
                      {signal ? (
                        <>
                          <Marke
                            stand={autoQaStand(signal.zustand)}
                            label={`${signal.automationName} · ${autoQaLabel(
                              signal.zustand,
                            )}`}
                          />
                          <Mikrolabel grad={10.5} className="tabular-nums">
                            {signal.kopfSha
                              ? `Kopf ${signal.kopfSha.slice(0, 8)}`
                              : "ohne prüfbare SHA"}
                          </Mikrolabel>
                          <Link
                            to="/auto-qa/auftraege/$auftragId"
                            params={{ auftragId: signal.id }}
                            className={cx(
                              "ml-auto font-mono text-[11px] text-akzent underline decoration-1 underline-offset-[3px]",
                              focusRing,
                            )}
                          >
                            Auftrag öffnen
                          </Link>
                        </>
                      ) : (
                        <Marke
                          stand="ausstehend"
                          label={
                            fehlgrund?.meldung ??
                            `Automation ${automationId.slice(0, 8)} · Auftrag fehlt`
                          }
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="flex min-h-11 items-center border-b border-linie py-2">
                <Marke stand="ausstehend" label="Keine Automation ausgewählt" />
              </div>
            )
          ) : (
            <div className="flex min-h-11 items-center border-b border-linie py-2">
              <Marke stand="uebersprungen" label="Auto Q&A nicht erforderlich" />
            </div>
          )}
        </div>
      </div>

      <div className="mt-5">
        <Mikrolabel grad={11.5} ton="laut" betont>
          Gründe
        </Mikrolabel>
        {daten.ergebnis.gruende.length > 0 ? (
          <ul className="mt-1" role="list">
            {daten.ergebnis.gruende.map((grund, index) => (
              <li
                key={`${grund.code}-${grund.bezugId ?? index}`}
                className="flex min-h-11 items-start gap-3 border-b border-linie py-2.5"
              >
                <Marke
                  stand={grundStand(grund.art)}
                  label={grundLabel(grund.art)}
                />
                <span className="min-w-0 text-[12.5px] leading-relaxed text-tinte2">
                  {grund.meldung}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-2 text-[12.5px] text-tinte3">
            Keine offenen Gründe.
          </p>
        )}
      </div>

      {daten.uebersteuerung ? (
        <div className="mt-5 border-l-2 border-tinte4 bg-wisch px-3.5 py-3">
          <Mikrolabel grad={11.5} ton="laut" betont>
            Begründet übersteuert
          </Mikrolabel>
          <p className="mt-1.5 whitespace-pre-wrap text-[12.5px] leading-relaxed text-tinte2">
            {daten.uebersteuerung.begruendung}
          </p>
          <Mikrolabel grad={10.5} className="mt-2 block">
            {formatiereZeitpunkt(daten.uebersteuerung.angelegtAm)}
            {daten.uebersteuerung.angelegtVon
              ? ` · Person ${daten.uebersteuerung.angelegtVon.slice(0, 8)}`
              : ""}
          </Mikrolabel>
        </div>
      ) : null}

      {pruefen.error ? (
        <div className="mt-4">
          <Fehlerzeile
            meldung={fehlerMeldung(pruefen.error)}
            onErneut={() => pruefen.mutate()}
          />
        </div>
      ) : null}

      {mergen.error && !mergeDialogOffen ? (
        <div className="mt-4">
          <Fehlerzeile meldung={fehlerMeldung(mergen.error)} />
        </div>
      ) : null}

      <div className="mt-5 flex flex-wrap items-center gap-3">
        {darfPruefen ? (
          <Button
            variant="secondary"
            onClick={() => pruefen.mutate()}
            isLoading={pruefen.isPending}
            loadingText="Prüfe …"
          >
            Neu prüfen
          </Button>
        ) : null}
        {darfMergen && istOffen ? (
          <Button
            variant="destructive"
            disabled={
              !daten.mergeRequest.kopfSha || !daten.ergebnis.darfMergen
            }
            onClick={() => {
              mergen.reset();
              setzeMergeWiederholungsschluessel(crypto.randomUUID());
              setzeMergeDialogOffen(true);
            }}
          >
            Merge Request mergen …
          </Button>
        ) : null}
        {istUebersteuerbar && darfUebersteuern ? (
          <Button
            variant="destructive"
            onClick={() => {
              if (!daten.aktuellePruefung) return;
              setzeAusnahmeZiel({
                pruefungId: daten.aktuellePruefung.id,
                kopfSha: daten.mergeRequest.kopfSha,
              });
              setzeDialogOffen(true);
            }}
          >
            Übersteuern …
          </Button>
        ) : null}
        {istUebersteuerbar && !darfUebersteuern ? (
          <Mikrolabel grad={11}>
            Übersteuern erfordert eine Schreibrolle und das Recht
            „gitlab.gate_uebersteuern“.
          </Mikrolabel>
        ) : null}
      </div>

      {darfMergen && istOffen && !daten.ergebnis.darfMergen ? (
        <Mikrolabel grad={11} className="mt-2 block">
          Der Merge bleibt deaktiviert, bis das Gate besteht oder eine
          zulässige Prüfung begründet übersteuert wurde.
        </Mikrolabel>
      ) : null}

      {daten.regel.modus === "blockierend" ? (
        <Mikrolabel grad={10.5} className="mt-3 block">
          Dieses Gate gilt für den Merge-Weg in Cubic. Ein direkter Merge in
          GitLab liegt außerhalb dieser Prüfung.
        </Mikrolabel>
      ) : null}

      <Dialog open={dialogOffen} onOpenChange={dialogAendern}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Quality Gate übersteuern</DialogTitle>
            <DialogDescription>
              {`Die Ausnahme gilt nur für diesen unveränderten Prüfschnappschuss${
                ausnahmeZiel?.kopfSha
                  ? ` und die Kopf-SHA ${ausnahmeZiel.kopfSha.slice(0, 8)}`
                  : " und diese Kopf-SHA"
              }. Eine neue Revision muss erneut geprüft werden.`}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-5">
            <Label htmlFor={`gate-begruendung-${mrId}`}>Begründung</Label>
            <Textarea
              id={`gate-begruendung-${mrId}`}
              className="mt-1.5 min-h-28"
              value={begruendung}
              onChange={(ereignis) => setzeBegruendung(ereignis.target.value)}
              minLength={10}
              maxLength={2_000}
              required
              aria-invalid={begruendung.length > 0 && !begruendungGueltig}
              aria-describedby={`gate-begruendung-hinweis-${mrId}`}
              hasError={begruendung.length > 0 && !begruendungGueltig}
              placeholder="Warum ist der Merge trotz des Gate-Ergebnisses vertretbar?"
            />
            <span
              id={`gate-begruendung-hinweis-${mrId}`}
              className="mt-1.5 flex justify-between gap-4"
            >
              <Mikrolabel grad={10.5}>
                Mindestens 10 Zeichen; die Begründung bleibt im Nachweis.
              </Mikrolabel>
              <Mikrolabel grad={10.5} className="tabular-nums">
                {begruendung.length}/2.000
              </Mikrolabel>
            </span>
          </div>
          {uebersteuern.error ? (
            <div className="mt-4">
              <Fehlerzeile meldung={fehlerMeldung(uebersteuern.error)} />
            </div>
          ) : null}
          <DialogFooter className="mt-5">
            <Button variant="secondary" onClick={() => dialogAendern(false)}>
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              disabled={!begruendungGueltig || !ausnahmeZiel}
              isLoading={uebersteuern.isPending}
              loadingText="Übersteuert …"
              onClick={() => {
                if (!ausnahmeZiel || !begruendungGueltig) return;
                uebersteuern.mutate(
                  {
                    pruefungId: ausnahmeZiel.pruefungId,
                    begruendung: begruendung.trim(),
                  },
                  { onSuccess: () => dialogAendern(false) },
                );
              }}
            >
              Begründet übersteuern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={mergeDialogOffen} onOpenChange={setzeMergeDialogOffen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Merge Request wirklich mergen?</DialogTitle>
            <DialogDescription>
              Cubic prüft das Gate unmittelbar erneut und sendet die erwartete
              Kopf-SHA an GitLab. Diese Aktion lässt sich nicht rückgängig
              machen.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-5 bg-wisch px-3.5 py-3 text-[12.5px] leading-relaxed text-tinte">
            !{daten.mergeRequest.gitlabIid} · {daten.mergeRequest.titel}
            <br />
            Kopf-SHA: {daten.mergeRequest.kopfSha?.slice(0, 12) ?? "fehlt"}
          </div>
          {mergen.error ? (
            <div className="mt-4">
              <Fehlerzeile meldung={fehlerMeldung(mergen.error)} />
            </div>
          ) : null}
          <DialogFooter className="mt-5">
            <Button
              variant="secondary"
              onClick={() => setzeMergeDialogOffen(false)}
            >
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              disabled={
                !daten.mergeRequest.kopfSha || !daten.ergebnis.darfMergen
              }
              isLoading={mergen.isPending}
              loadingText="Wird gemergt …"
              onClick={() => {
                if (
                  !daten.mergeRequest.kopfSha ||
                  !daten.ergebnis.darfMergen
                ) {
                  return;
                }
                mergen.mutate(
                  {
                    erwarteteKopfSha: daten.mergeRequest.kopfSha,
                    wiederholungsschluessel:
                      mergeWiederholungsschluessel,
                  },
                  {
                    onSuccess: () => {
                      setzeMergeDialogOffen(false);
                      setzeMergeWiederholungsschluessel("");
                    },
                  },
                );
              }}
            >
              Jetzt in GitLab mergen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
